import { action, computed, observable } from "mobx";

import { RefDataDto } from "@libs/api/ref-data/dto.ts";
import { ConvertToRefData } from "@libs/api/ref-data/RefData.ts";
import { BusinessRole } from "@libs/gateways/core/CoreGateway.dtos.ts";
import {
  AccProviderTypeText,
  NzPracticeAccContractDto,
  ProviderAccContractTypeDto
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import {
  AccPracticeContractLabels,
  AccPracticeContractType
} from "@modules/settings/screens/practice/components/practice-details/PracticeDetailsForm.types.tsx";
import { User } from "@stores/core/models/User.ts";
import { PracticeStore } from "@stores/practice/PracticeStore.ts";
import { RootStore } from "@stores/root/RootStore.ts";

import {
  AccContractFormValues,
  ContractTypeFormValues
} from "../AccContractForm.types.ts";
import { NO_ACC_CONTRACT } from "../ProviderAccContracts.types.ts";

export class ProviderAccContractHelper {
  private practice: PracticeStore;

  constructor(
    private root: RootStore,
    private user: User
  ) {
    this.practice = this.root.practice;
  }

  @observable
  public originalPracticeAccContractCompanyData: NzPracticeAccContractDto[] =
    [];

  @action
  public setAvailableAccContracts(data: NzPracticeAccContractDto[]) {
    this.originalPracticeAccContractCompanyData = data;
  }

  public onSubmit = async (values: AccContractFormValues) => {
    const filteredValues = values.contractTypes.filter(
      x => !x.providerContractTypeCode.includes(NO_ACC_CONTRACT)
    );

    const provider = await this.practice.getProvider(this.user.id);

    await this.practice.updateProvider({
      id: this.user.id,
      ahpraNumber: provider.ahpraNumber,
      medicareProviderNumber: provider.medicareProviderNumber,
      discipline: provider.discipline,
      cpn: provider.cpn,
      accProviderContractTypeCodes: filteredValues.map(
        c => c.providerContractTypeCode
      )
    });
  };
  private getAccContractSubtext(
    typeCode: string | undefined,
    businessRoleCode: string | undefined
  ) {
    if (!typeCode || !businessRoleCode) return undefined;

    const practiceAccContractData =
      this.originalPracticeAccContractCompanyData.find(data => {
        if (
          data.accPracticeContractTypeCode ===
          AccPracticeContractType.SpecifiedTreatmentProvider
        ) {
          return data.businessRoleCode === businessRoleCode;
        } else {
          return data.accPracticeContractTypeCode === typeCode;
        }
      });

    if (practiceAccContractData?.contractId)
      return `${
        AccPracticeContractLabels[
          practiceAccContractData.accPracticeContractTypeCode as
            | "STP"
            | "AHS"
            | "ORS"
            | "BR"
        ]
      }: ${practiceAccContractData?.contractId}`;
    return undefined;
  }

  public getProviderAccContractType(
    practiceContractTypes: NzPracticeAccContractDto[]
  ): ProviderAccContractTypeDto[] {
    return this.availableAccProviderContractTypes.filter(providerType =>
      practiceContractTypes.find(practiceType => {
        if (
          practiceType.accPracticeContractTypeCode !==
          providerType.accPracticeContractTypeCode
        ) {
          return false;
        } else if (!practiceType.businessRoleCode) {
          return true;
        }
        return practiceType.businessRoleCode === providerType.businessRoleCode;
      })
    );
  }

  public getAccContractTypeFormValues(
    contractTypes?: string[]
  ): ContractTypeFormValues[] {
    let result: ContractTypeFormValues[] =
      contractTypes?.map(code => {
        const providerACC =
          this.practice.ref.accProviderContractTypes.get(code);
        return {
          providerTypeCode: providerACC?.providerTypeCode,
          providerTypeText: providerACC?.providerTypeText,
          providerContractTypeCode: code,
          subtext: this.getAccContractSubtext(
            providerACC?.accPracticeContractTypeCode,
            providerACC?.businessRoleCode
          )
        };
      }) ?? [];

    const possibleAccContracts =
      this.practice.ref.accProviderContractTypes.values.filter(x =>
        this.user.businessRoles.includes(x.businessRoleCode)
      );

    possibleAccContracts.forEach(contractCode => {
      const providerACC = this.practice.ref.accProviderContractTypes.get(
        contractCode.code
      );
      if (
        !result.some(x => {
          return x.providerTypeText === contractCode.providerTypeText;
        })
      ) {
        result?.push({
          providerTypeCode: contractCode.providerTypeCode,
          providerTypeText: contractCode.providerTypeText,
          providerContractTypeCode: `${contractCode.providerTypeText}${NO_ACC_CONTRACT}`,
          subtext: this.getAccContractSubtext(
            providerACC?.accPracticeContractTypeCode,
            providerACC?.businessRoleCode
          )
        });
      }
    });

    if (this.user.businessRoles.includes(BusinessRole.HandTherapist)) {
      result = result.filter(
        x =>
          x.providerTypeText !==
          (this.user.businessRoles.includes(BusinessRole.Physiotherapist)
            ? AccProviderTypeText.HandTherapistOT
            : AccProviderTypeText.HandTherapistPhysio)
      );
    }
    return result;
  }

  @computed
  get availableAccProviderContractTypes(): ProviderAccContractTypeDto[] {
    return this.practice.ref.accProviderContractTypes.values
      .filter(p => this.user.businessRoles.some(x => x === p.businessRoleCode))
      .map(value => ({
        ...value,
        isActive: this.originalPracticeAccContractCompanyData.some(
          p =>
            p.accPracticeContractTypeCode === value.accPracticeContractTypeCode
        )
      }));
  }

  @computed
  get availableAccProviderTypes() {
    return this.availableAccProviderContractTypes
      .filter((value, index, array) => {
        return (
          index ===
          array.findIndex(
            y =>
              y.providerTypeCode === value.providerTypeCode &&
              y.providerTypeText === value.providerTypeText
          )
        );
      })
      .map(x => {
        const providerType: ConvertToRefData<RefDataDto> = {
          code: x.providerTypeCode,
          text: x.providerTypeText
        };

        return providerType;
      })
      .filter(x => {
        if (this.user.businessRoles.includes(BusinessRole.HandTherapist)) {
          return (
            x.text !==
            (this.user.businessRoles.includes(BusinessRole.Physiotherapist)
              ? AccProviderTypeText.HandTherapistOT
              : AccProviderTypeText.HandTherapistPhysio)
          );
        }
        return true;
      });
  }
}
